import {
  getChatMessages,
  getChatUsers,
  getUnreadMessageCount,
  markMessagesAsRead,
  sendMessage,
  subscribeToOnlineStatus,
  supabase,
  updateOnlineStatus,
} from '@/api/apiChat';
import { ChatHeader } from '@/components/chat/ChatHeader';
import { ChatInput } from '@/components/chat/ChatInput';
import { ChatMessageList } from '@/components/chat/ChatMessageList';
import { ChatUserList } from '@/components/chat/ChatUserList';
import { useUser } from '@/lib/hooks/useUser';
import { showNotification } from '@/lib/notifications';
import type { Employee, Message } from '@/types/chat';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

export default function ChatPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<Employee | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);

  // Query for chat users
  const { data: users = [] } = useQuery<Employee[]>({
    queryKey: ['chatUsers'],
    queryFn: getChatUsers,
    enabled: !!user?.id,
    select: data => data.filter(u => u.empUserId !== user?.id),
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Query for online users
  const { data: onlineUsers = new Set<string>() } = useQuery<Set<string>>({
    queryKey: ['onlineUsers'],
    queryFn: async () => {
      const { data } = await supabase
        .from('employees')
        .select('empUserId, is_online')
        .eq('is_online', true);
      return new Set((data || []).filter(u => u.is_online).map(u => u.empUserId));
    },
    enabled: !!user?.id,
    refetchInterval: 3000, // Refresh every second
  });

  // Query for unread message counts
  const { data: unreadCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['unreadCounts', user?.id],
    queryFn: async () => {
      if (!user?.id || !users.length) return {};
      const counts: Record<string, number> = {};
      for (const chatUser of users) {
        counts[chatUser.empUserId] = await getUnreadMessageCount(user.id, chatUser.empUserId);
      }
      return counts;
    },
    enabled: !!user?.id && users.length > 0,
    refetchInterval: 3000, // Refresh every second
  });

  // Query to get current employee data
  useQuery<Employee>({
    queryKey: ['currentEmployee', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('No user ID');
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('empUserId', user.id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Employee not found');

      setCurrentEmployee(data);
      return data;
    },
    enabled: !!user?.id,
  });

  // Query for chat messages with selected user
  const { data: messages = [] } = useQuery<Message[]>({
    queryKey: ['messages', user?.id, selectedUser?.empUserId],
    queryFn: async () => {
      if (!user?.id || !selectedUser) return [];
      const messages = await getChatMessages(user.id, selectedUser.empUserId);

      // Mark messages as read
      const unreadMessageIds = messages
        .filter(msg => msg.receiver_id === user.id && !msg.read)
        .map(msg => msg.id);

      if (unreadMessageIds.length > 0) {
        await markMessagesAsRead(unreadMessageIds);
        queryClient.setQueryData(
          ['unreadCounts', user.id],
          (prev: Record<string, number> = {}) => ({
            ...prev,
            [selectedUser.empUserId]: 0,
          })
        );
      }

      return messages;
    },
    enabled: !!user?.id && !!selectedUser,
    refetchInterval: 3000, // Refresh every second
  });

  // Mutation for sending messages
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!user?.id || !selectedUser || !content.trim()) return null;
      return sendMessage(user.id, selectedUser.empUserId, content.trim());
    },
    onSuccess: message => {
      if (message) {
        queryClient.setQueryData(
          ['messages', user?.id, selectedUser?.empUserId],
          (prev: Message[] = []) => [...prev, message]
        );
        setNewMessage('');
      }
    },
    onError: () => {
      toast.error('Failed to send message');
    },
  });

  // Set online status and subscribe to changes
  useEffect(() => {
    if (!user?.id) return;

    // Mark self as online
    updateOnlineStatus(user.id, true);

    // Subscribe to online status changes
    const channel = subscribeToOnlineStatus(payload => {
      if (!payload.new) return;
      queryClient.setQueryData(['onlineUsers'], (prev: Set<string> = new Set()) => {
        const newSet = new Set(prev);
        if (payload.new.is_online) {
          newSet.add(payload.new.empUserId);
        } else {
          newSet.delete(payload.new.empUserId);
        }
        return newSet;
      });
    });

    // Subscribe to new messages
    const messageChannel = supabase
      .channel('new_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`,
        },
        async payload => {
          const newMessage = payload.new as Message;
          const sender = users.find(u => u.empUserId === newMessage.sender_id);

          if (selectedUser?.empUserId === newMessage.sender_id) {
            // Update messages if sender is selected
            queryClient.setQueryData(
              ['messages', user.id, selectedUser.empUserId],
              (prev: Message[] = []) => [...prev, newMessage]
            );
            await markMessagesAsRead([newMessage.id]);
          } else {
            // Update unread counts
            queryClient.setQueryData(
              ['unreadCounts', user.id],
              (prev: Record<string, number> = {}) => ({
                ...prev,
                [newMessage.sender_id]: (prev[newMessage.sender_id] || 0) + 1,
              })
            );
          }

          // Show notification
          await showNotification(`New message from ${sender?.empName || 'Someone'}`, {
            body: newMessage.content,
            tag: 'new-message',
          });
        }
      )
      .subscribe();

    // Cleanup
    return () => {
      updateOnlineStatus(user.id, false);
      channel.unsubscribe();
      messageChannel.unsubscribe();
    };
  }, [user?.id, queryClient, selectedUser, users]);

  // Register service worker for notifications
  useEffect(() => {
    const registerServiceWorker = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js');
          console.log('Service Worker registered:', registration);
        } catch (error) {
          console.error('Service Worker registration failed:', error);
        }
      }
    };
    registerServiceWorker();
  }, []);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    sendMessageMutation.mutate(newMessage);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">Please log in to access chat.</div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white">
      <ChatUserList
        users={users}
        selectedUser={selectedUser}
        onSelectUser={setSelectedUser}
        onlineUsers={onlineUsers}
        unreadCounts={unreadCounts}
      />
      {selectedUser ? (
        <div className="flex-1 flex flex-col">
          <ChatHeader user={selectedUser} isOnline={onlineUsers.has(selectedUser.empUserId)} />
          {selectedUser && currentEmployee ? (
            <ChatMessageList messages={messages} currentUser={currentEmployee} />
          ) : null}
          <ChatInput
            value={newMessage}
            onChange={setNewMessage}
            onSubmit={handleSendMessage}
            isLoading={sendMessageMutation.isPending}
          />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          Select a user to start chatting
        </div>
      )}
    </div>
  );
}
